import { useState, useCallback } from "react";
import { getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  PhoneCall, Trophy, Clock, RefreshCw, RotateCcw, ChevronRight,
  ChevronDown, Star, Filter, X,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CallsStats {
  total: number;
  winning: number;
  pendingScore: number;
  lastIngestedAt: string | null;
}

interface TranscriptTurn {
  role: string;
  text: string;
  timestamp_seconds: number;
}

interface ScoreDimension {
  score: number;
  label: string;
  moments: Array<{ timestamp_seconds: number; note: string }>;
}

interface AiScores {
  scores: {
    talk_ratio: ScoreDimension;
    discovery: ScoreDimension;
    objection_handling: ScoreDimension;
    value_delivery: ScoreDimension;
    closing: ScoreDimension;
    rapport: ScoreDimension;
  };
  overall_score: number;
  tags: string[];
}

interface SalesTrainingCall {
  id: string;
  workspaceId: string;
  sessionId: string;
  userId: string;
  leadId: string | null;
  clientId: string | null;
  clientType: string | null;
  transcript: TranscriptTurn[];
  turnCount: number;
  durationSeconds: number;
  tags: string[];
  aiScores: AiScores | null;
  isWinningCall: boolean;
  status: "pending" | "scored" | "failed";
  scoredAt: string | null;
  createdAt: string;
}

interface CallsPage {
  rows: SalesTrainingCall[];
  nextCursor: string | null;
}

interface WorkspaceOption {
  id: string;
  name: string;
  slug: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDuration(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: getWorkspaceTimeZone(),
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }) + " " + getTimeZoneAbbreviation(iso);
}

function scoreColor(score: number): string {
  if (score >= 75) return "text-green-600 dark:text-green-400";
  if (score >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function scoreBarColor(score: number): string {
  if (score >= 75) return "bg-green-500";
  if (score >= 50) return "bg-amber-500";
  return "bg-red-500";
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  scored: "Scored",
  failed: "Failed",
};

const STATUS_VARIANT: Record<string, "secondary" | "default" | "destructive"> = {
  pending: "secondary",
  scored: "default",
  failed: "destructive",
};

const DIMENSIONS: { key: keyof AiScores["scores"]; label: string }[] = [
  { key: "talk_ratio", label: "Talk Ratio" },
  { key: "discovery", label: "Discovery" },
  { key: "objection_handling", label: "Objection Handling" },
  { key: "value_delivery", label: "Value Delivery" },
  { key: "closing", label: "Closing" },
  { key: "rapport", label: "Rapport" },
];

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({
  title, value, icon: Icon, description, isLoading,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
  isLoading?: boolean;
}) {
  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="text-2xl font-bold" data-testid={`value-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            {value}
          </div>
        )}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Transcript Panel ─────────────────────────────────────────────────────────

function TranscriptPanel({ transcript }: { transcript: TranscriptTurn[] }) {
  if (!transcript || transcript.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No transcript captured.</p>;
  }
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="transcript-turns">
      {transcript.map((turn, i) => (
        <div key={i} className="flex gap-3">
          <span className="text-xs text-muted-foreground w-12 shrink-0 pt-0.5">
            {Math.floor(turn.timestamp_seconds / 60)}:{String(turn.timestamp_seconds % 60).padStart(2, "0")}
          </span>
          <div className="flex-1">
            <span className={`text-xs font-semibold mr-2 ${turn.role === "user" ? "text-blue-600 dark:text-blue-400" : "text-violet-600 dark:text-violet-400"}`}>
              {turn.role === "user" ? "REP" : "AI"}
            </span>
            <span className="text-sm">{turn.text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Score Breakdown Panel ────────────────────────────────────────────────────

function ScoreBreakdown({ aiScores }: { aiScores: AiScores }) {
  return (
    <div className="space-y-4" data-testid="score-breakdown">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Overall Score</span>
        <span className={`text-2xl font-bold ${scoreColor(aiScores.overall_score)}`}>
          {aiScores.overall_score}
        </span>
        <span className="text-sm text-muted-foreground">/ 100</span>
      </div>
      {aiScores.tags && aiScores.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {aiScores.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">{tag.replace(/_/g, " ")}</Badge>
          ))}
        </div>
      )}
      <Separator />
      <div className="space-y-3">
        {DIMENSIONS.map(({ key, label }) => {
          const dim = aiScores.scores?.[key];
          if (!dim) return null;
          return (
            <div key={key} data-testid={`dim-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground italic">{dim.label}</span>
                  <span className={`text-sm font-bold tabular-nums ${scoreColor(dim.score)}`}>{dim.score}</span>
                </div>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${scoreBarColor(dim.score)}`}
                  style={{ width: `${dim.score}%` }}
                />
              </div>
              {dim.moments && dim.moments.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {dim.moments.map((m, i) => (
                    <p key={i} className="text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                      <span className="font-mono text-[10px] mr-1">
                        {Math.floor(m.timestamp_seconds / 60)}:{String(m.timestamp_seconds % 60).padStart(2, "0")}
                      </span>
                      {m.note}
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Call Detail Drawer ───────────────────────────────────────────────────────

function CallDrawer({
  call,
  open,
  onClose,
  onRescore,
  isRescoring,
}: {
  call: SalesTrainingCall | null;
  open: boolean;
  onClose: () => void;
  onRescore: (id: string) => void;
  isRescoring: boolean;
}) {
  const [tab, setTab] = useState<"scores" | "transcript">("scores");

  if (!call) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="call-detail-drawer">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4" />
            Call Detail
            {call.isWinningCall && (
              <Badge className="bg-amber-500 text-white text-xs ml-1">
                <Trophy className="h-3 w-3 mr-1" /> Winning
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Workspace</span>
              <p className="font-mono text-xs truncate">{call.workspaceId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Session</span>
              <p className="font-mono text-xs truncate">{call.sessionId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Date</span>
              <p>{fmtDate(call.createdAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Duration</span>
              <p>{fmtDuration(call.durationSeconds)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status</span>
              <Badge variant={STATUS_VARIANT[call.status] ?? "secondary"} className="mt-0.5">
                {STATUS_LABELS[call.status] ?? call.status}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Turns</span>
              <p>{call.turnCount}</p>
            </div>
          </div>

          {call.tags && call.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {call.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">{tag.replace(/_/g, " ")}</Badge>
              ))}
            </div>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={() => onRescore(call.id)}
            disabled={isRescoring}
            data-testid="button-rescore"
            className="w-full"
          >
            <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${isRescoring ? "animate-spin" : ""}`} />
            {isRescoring ? "Queuing re-score…" : "Re-score with Claude"}
          </Button>

          <Separator />

          {/* Tabs */}
          <div className="flex rounded-md border overflow-hidden">
            <button
              onClick={() => setTab("scores")}
              className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors ${tab === "scores" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="tab-scores"
            >
              AI Scores
            </button>
            <button
              onClick={() => setTab("transcript")}
              className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors ${tab === "transcript" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="tab-transcript"
            >
              Transcript
            </button>
          </div>

          {tab === "scores" ? (
            call.aiScores ? (
              <ScoreBreakdown aiScores={call.aiScores} />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {call.status === "pending"
                  ? "Awaiting Claude analysis…"
                  : "Scoring failed — re-score to retry."}
              </p>
            )
          ) : (
            <TranscriptPanel transcript={call.transcript} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

interface Filters {
  workspaceId: string;
  status: string;
  winningOnly: boolean;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: Filters = {
  workspaceId: "",
  status: "",
  winningOnly: false,
  dateFrom: "",
  dateTo: "",
};

function FilterBar({
  filters,
  onChange,
  workspaces,
  wsLoading,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  workspaces: WorkspaceOption[];
  wsLoading: boolean;
}) {
  const active =
    filters.workspaceId || filters.status || filters.winningOnly || filters.dateFrom || filters.dateTo;

  return (
    <Card data-testid="filter-bar">
      <CardContent className="pt-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Filter className="h-4 w-4 text-muted-foreground self-center mb-1 shrink-0" />

          {/* Workspace */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <Label className="text-xs">Workspace</Label>
            <Select
              value={filters.workspaceId || "__all__"}
              onValueChange={(v) => onChange({ ...filters, workspaceId: v === "__all__" ? "" : v })}
              disabled={wsLoading}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="select-workspace">
                <SelectValue placeholder="All workspaces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All workspaces</SelectItem>
                {workspaces.map((ws) => (
                  <SelectItem key={ws.id} value={ws.id}>{ws.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1 min-w-[130px]">
            <Label className="text-xs">Status</Label>
            <Select
              value={filters.status || "__all__"}
              onValueChange={(v) => onChange({ ...filters, status: v === "__all__" ? "" : v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="select-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scored">Scored</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date from */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              className="h-8 text-xs w-36"
              value={filters.dateFrom}
              onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })}
              data-testid="input-date-from"
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              className="h-8 text-xs w-36"
              value={filters.dateTo}
              onChange={(e) => onChange({ ...filters, dateTo: e.target.value })}
              data-testid="input-date-to"
            />
          </div>

          {/* Winning only toggle */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Winning only</Label>
            <Button
              size="sm"
              variant={filters.winningOnly ? "default" : "outline"}
              className="h-8 gap-1.5 text-xs"
              onClick={() => onChange({ ...filters, winningOnly: !filters.winningOnly })}
              data-testid="button-winning-only"
            >
              <Trophy className="h-3 w-3" />
              {filters.winningOnly ? "On" : "Off"}
            </Button>
          </div>

          {/* Clear */}
          {active && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs gap-1 text-muted-foreground self-end"
              onClick={() => onChange(EMPTY_FILTERS)}
              data-testid="button-clear-filters"
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Call Row ─────────────────────────────────────────────────────────────────

function CallRow({
  call,
  onSelect,
  onRescore,
  isRescoring,
  wsName,
}: {
  call: SalesTrainingCall;
  onSelect: () => void;
  onRescore: () => void;
  isRescoring: boolean;
  wsName: string;
}) {
  const overall = call.aiScores?.overall_score ?? null;

  return (
    <tr
      className="border-b hover:bg-muted/40 cursor-pointer transition-colors"
      onClick={onSelect}
      data-testid={`row-call-${call.id}`}
    >
      <td className="px-4 py-3 text-xs text-muted-foreground max-w-[140px]">
        <span className="truncate block font-medium text-foreground">{wsName || call.workspaceId.slice(0, 8) + "…"}</span>
      </td>
      <td className="px-4 py-3 text-xs font-mono text-muted-foreground max-w-[100px]">
        <span className="truncate block">{call.userId.slice(0, 12)}…</span>
      </td>
      <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(call.createdAt)}</td>
      <td className="px-4 py-3 text-xs tabular-nums">{fmtDuration(call.durationSeconds)}</td>
      <td className="px-4 py-3">
        <Badge variant={STATUS_VARIANT[call.status] ?? "secondary"} className="text-xs">
          {STATUS_LABELS[call.status] ?? call.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-center">
        {overall !== null ? (
          <span className={`text-sm font-bold tabular-nums ${scoreColor(overall)}`}>{overall}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {call.isWinningCall ? (
          <Trophy className="h-4 w-4 text-amber-500 mx-auto" data-testid={`badge-winning-${call.id}`} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={(e) => { e.stopPropagation(); onRescore(); }}
          disabled={isRescoring}
          data-testid={`button-rescore-${call.id}`}
          title="Re-score with Claude"
        >
          <RotateCcw className={`h-3 w-3 ${isRescoring ? "animate-spin" : ""}`} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          data-testid={`button-open-${call.id}`}
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      </td>
    </tr>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function CallsContent() {
  const { toast } = useToast();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursors, setCursors] = useState<string[]>([]);
  const [selectedCall, setSelectedCall] = useState<SalesTrainingCall | null>(null);
  const [rescoringIds, setRescoringIds] = useState<Set<string>>(new Set());

  // Stats
  const { data: stats, isLoading: statsLoading } = useQuery<CallsStats>({
    queryKey: ["/api/platform/calls/stats"],
  });

  // Workspaces for filter dropdown (light subset)
  const { data: wsData, isLoading: wsLoading } = useQuery<WorkspaceOption[]>({
    queryKey: ["/api/platform/workspaces"],
    select: (data: any) => (Array.isArray(data?.workspaces) ? data.workspaces : Array.isArray(data) ? data : []),
  });
  const workspaces: WorkspaceOption[] = wsData ?? [];

  // Build query params
  const buildParams = useCallback((f: Filters, cur: string | null) => {
    const p = new URLSearchParams();
    if (f.workspaceId) p.set("workspaceId", f.workspaceId);
    if (f.status) p.set("status", f.status);
    if (f.winningOnly) p.set("winningOnly", "true");
    if (f.dateFrom) p.set("dateFrom", new Date(f.dateFrom).toISOString());
    if (f.dateTo) {
      const d = new Date(f.dateTo);
      d.setHours(23, 59, 59, 999);
      p.set("dateTo", d.toISOString());
    }
    if (cur) p.set("cursor", cur);
    return p.toString();
  }, []);

  const queryParams = buildParams(filters, cursor);

  const { data: page, isLoading: pageLoading } = useQuery<CallsPage>({
    queryKey: ["/api/platform/calls", queryParams],
    queryFn: async () => {
      const res = await fetch(`/api/platform/calls?${queryParams}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load calls");
      return res.json();
    },
  });

  // Re-score mutation
  const rescoreMutation = useMutation({
    mutationFn: async (callId: string) => {
      await apiRequest("POST", `/api/platform/calls/${callId}/rescore`);
    },
    onMutate: (callId: string) => {
      setRescoringIds((s) => new Set([...s, callId]));
    },
    onSuccess: (_data, callId) => {
      setRescoringIds((s) => { const n = new Set(s); n.delete(callId); return n; });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/calls"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/calls/stats"] });
      toast({ title: "Re-score queued", description: "Claude will re-analyze this call shortly." });
    },
    onError: (_err, callId) => {
      setRescoringIds((s) => { const n = new Set(s); n.delete(callId); return n; });
      toast({ title: "Failed to queue re-score", variant: "destructive" });
    },
  });

  const handleFilterChange = (f: Filters) => {
    setFilters(f);
    setCursor(null);
    setCursors([]);
  };

  const goNext = () => {
    if (!page?.nextCursor) return;
    setCursors((c) => [...c, cursor ?? ""]);
    setCursor(page.nextCursor);
  };

  const goPrev = () => {
    if (cursors.length === 0) return;
    const prev = cursors[cursors.length - 1];
    setCursors((c) => c.slice(0, -1));
    setCursor(prev || null);
  };

  const lastIngested = stats?.lastIngestedAt
    ? fmtDate(stats.lastIngestedAt)
    : "—";

  // Build workspace id→name map for display
  const wsMap = Object.fromEntries(workspaces.map((ws) => [ws.id, ws.name]));

  const calls = page?.rows ?? [];
  const page0 = cursors.length === 0 && !cursor;

  return (
    <>
      <div className="p-6 space-y-6" data-testid="page-platform-calls">
        {/* Header */}
        <div className="flex items-center gap-3">
          <PhoneCall className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Calls</h1>
            <p className="text-sm text-muted-foreground">
              Browse captured voice-assistant sessions, AI scores, and transcripts.
            </p>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Captured"
            value={stats?.total ?? 0}
            icon={PhoneCall}
            description="Voice sessions recorded"
            isLoading={statsLoading}
          />
          <StatCard
            title="Winning Calls"
            value={stats?.winning ?? 0}
            icon={Trophy}
            description="Linked to converted leads"
            isLoading={statsLoading}
          />
          <StatCard
            title="Pending Scoring"
            value={stats?.pendingScore ?? 0}
            icon={Clock}
            description="Awaiting Claude analysis"
            isLoading={statsLoading}
          />
          <StatCard
            title="Last Ingestion"
            value={lastIngested}
            icon={RefreshCw}
            description="Most recent capture (CT)"
            isLoading={statsLoading}
          />
        </div>

        {/* Filter Bar */}
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          workspaces={workspaces}
          wsLoading={wsLoading}
        />

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="calls-table">
              <thead>
                <tr className="border-b bg-muted/30 text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="px-4 py-3 text-left font-medium">Workspace</th>
                  <th className="px-4 py-3 text-left font-medium">Rep</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Duration</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-center font-medium">Score</th>
                  <th className="px-4 py-3 text-center font-medium">
                    <Trophy className="h-3 w-3 inline" />
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : calls.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground text-sm">
                      No calls found. Adjust your filters or wait for new sessions to be captured.
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => (
                    <CallRow
                      key={call.id}
                      call={call}
                      wsName={wsMap[call.workspaceId] ?? ""}
                      onSelect={() => setSelectedCall(call)}
                      onRescore={() => rescoreMutation.mutate(call.id)}
                      isRescoring={rescoringIds.has(call.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(calls.length > 0 || !page0) && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-muted-foreground">
                {calls.length > 0 ? `${calls.length} result${calls.length === 1 ? "" : "s"} on this page` : ""}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page0}
                  onClick={goPrev}
                  data-testid="button-prev-page"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!page?.nextCursor}
                  onClick={goNext}
                  data-testid="button-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Info footer */}
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground space-y-2">
            <p>
              <strong>How this works:</strong> When any voice-assistant session ends on the platform, the transcript
              is captured automatically — no workspace opt-in required. Claude then scores each call across 6 sales
              dimensions (Talk Ratio, Discovery, Objection Handling, Value Delivery, Closing, Rapport).
            </p>
            <p>
              When a lead converts to a client, all prior calls linked to that lead are retroactively flagged as
              "winning calls" and re-scored with outcome context. Over time, this builds a dataset of patterns that
              precede successful conversions.
            </p>
            <p className="text-xs">
              <strong>Note:</strong> This data is platform-only — no workspace user can see these records or scores.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detail drawer */}
      <CallDrawer
        call={selectedCall}
        open={!!selectedCall}
        onClose={() => setSelectedCall(null)}
        onRescore={(id) => rescoreMutation.mutate(id)}
        isRescoring={!!selectedCall && rescoringIds.has(selectedCall.id)}
      />
    </>
  );
}

export default function PlatformCallsPage() {
  return (
    <PlatformLayout>
      <CallsContent />
    </PlatformLayout>
  );
}
